/**
 * Admin-only prompt fetch service.
 * Fetches prompts without organization filter so admins see their own and discovered
 * prompts across all orgs. Do not use for normal users.
 */

import * as promptLibraryService from './promptLibraryService';
import { Prompt } from '../../domain/models/Prompt';
import { fetchPrompts, fetchHiddenPromptIds } from '../../../../shared/data/promptsRepo';

/**
 * Get all prompts for admin (cross-org; Firestore rules gate this to
 * admin-domain users). Used internally.
 * @returns {Promise<Array<Prompt>>}
 */
const getAllPromptsForAdmin = async () => {
  const data = await fetchPrompts({ promptType: 'all', adminView: true });
  return data.map((p) => Prompt.fromData(p));
};

/**
 * Get admin's own prompts (created by userId) across all orgs.
 * @param {string} userId - The admin user ID
 * @returns {Promise<Array<Prompt>>} Array of user's prompts
 */
export const getAdminUserPrompts = async (userId) => {
  try {
    const prompts = await getAllPromptsForAdmin();
    const userPrompts = prompts.filter((p) => p.creatorId === userId);
    const hiddenSet = new Set(await fetchHiddenPromptIds());
    return userPrompts.filter((p) => !hiddenSet.has(p.id));
  } catch (error) {
    console.error('Error fetching admin user prompts:', error);
    throw error;
  }
};

/**
 * Get admin's favorite prompts across all orgs (no org filter).
 * @param {string} userId - The admin user ID
 * @returns {Promise<Array<Prompt>>} Array of favorite prompts
 */
export const getAdminFavoritePrompts = async (userId) => {
  return promptLibraryService.getFavoritePrompts(userId, null);
};

/**
 * Get prompts for admin Discover view: all prompts minus favorites and own.
 * @param {string} userId - The admin user ID
 * @param {Array} favoritePromptsList - List of favorite prompt objects (with id)
 * @param {Array} ownPromptsList - List of own prompt objects (with id)
 * @returns {Promise<Array<Prompt>>} Array of discover prompts
 */
export const getAdminDiscoverPrompts = async (userId, favoritePromptsList = [], ownPromptsList = []) => {
  try {
    const allPromptsData = await getAllPromptsForAdmin(userId);
    const allPromptsAsPlainObjects = allPromptsData.map((p) =>
      p.toJSON ? p.toJSON() : { ...p, categories: p.categories || [], category: p.category || '' }
    );
    const favoriteIds = new Set(favoritePromptsList.map((p) => p.id));
    const ownIds = new Set(ownPromptsList.map((p) => p.id));
    return allPromptsAsPlainObjects.filter((p) => !favoriteIds.has(p.id) && !ownIds.has(p.id));
  } catch (error) {
    console.error('[adminPromptFetchService] Error loading discover prompts:', error);
    throw error;
  }
};
